// Admin Settings → Evrst Printpage. Every editable text field on the public
// prints.evrststudio.com homepage, surfaced as a collapsible section per
// block. Persists to site_settings.prints_page via PUT /api/admin/settings/:key.
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'
import { PRINTS_PAGE_DEFAULTS, PrintsPageContent, mergePrintsContent } from '../../lib/prints-page-content'
import { useToast, ToastBanner } from '../../components/Toast'

export default function SettingsPrintsPage() {
  const { getAccessToken } = useAuth()
  const [c, setC] = useState<PrintsPageContent>(PRINTS_PAGE_DEFAULTS)
  const [err, setErr] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { toast, show } = useToast()

  useEffect(() => {
    (async () => {
      try {
        const d = await apiFetch<{ value: unknown; updatedAt: string | null }>(
          getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/settings/prints_page',
        )
        setC(mergePrintsContent(d.value))
        setSavedAt(d.updatedAt)
      } catch (e) { setErr((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function save() {
    setBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/settings/prints_page', {
        method: 'PUT', body: JSON.stringify({ value: c }),
      })
      setSavedAt(new Date().toISOString())
      show('Print page saved. Changes are live now on prints.evrststudio.com.')
    } catch (e) {
      show((e as Error).message, 'error', 5000)
    }
    finally { setBusy(false) }
  }
  function resetToDefaults() {
    if (!confirm('Reset every field to the built-in defaults? Your edits will be lost on save.')) return
    setC(JSON.parse(JSON.stringify(PRINTS_PAGE_DEFAULTS)))
  }

  // Tiny helper to update nested paths immutably without a dependency.
  function set<K extends keyof PrintsPageContent>(key: K, value: PrintsPageContent[K]) {
    setC((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="p-8">
      <ToastBanner toast={toast} />
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="eyebrow">Settings</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Evrst Printpage</h1>
          <p className="text-ink-2 text-sm mt-1 max-w-2xl">Every editable text field on the public prints.evrststudio.com homepage. Changes apply on the next page load (the homepage reads <span className="font-mono text-xs">/api/site-content/prints_page</span> on mount).</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-3">{savedAt ? `Saved ${new Date(savedAt).toLocaleString()}` : 'Unsaved'}</span>
          <button onClick={resetToDefaults} className="btn-outline text-xs">Reset</button>
          <button onClick={save} disabled={busy} className="btn-gold">{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="space-y-3 max-w-4xl">
        <Section title="Hero">
          <Grid2>
            <Field label="Eyebrow"               value={c.hero.eyebrow}            onChange={(v) => set('hero', { ...c.hero, eyebrow: v })} />
            <Field label="Title (line 1)"        value={c.hero.titleLine1}         onChange={(v) => set('hero', { ...c.hero, titleLine1: v })} />
          </Grid2>
          <Field label="Title (line 2, italic gold)" value={c.hero.titleLine2}     onChange={(v) => set('hero', { ...c.hero, titleLine2: v })} />
          <Textarea label="Body" value={c.hero.body} onChange={(v) => set('hero', { ...c.hero, body: v })} />
          <Grid2>
            <Field label="Primary CTA label"     value={c.hero.primaryCtaLabel}    onChange={(v) => set('hero', { ...c.hero, primaryCtaLabel: v })} />
            <Field label="Secondary CTA label"   value={c.hero.secondaryCtaLabel}  onChange={(v) => set('hero', { ...c.hero, secondaryCtaLabel: v })} />
          </Grid2>
          <ImageField label="Hero image (right column, in the browser mockup)" value={c.hero.imageUrl}
            onChange={(v) => set('hero', { ...c.hero, imageUrl: v })}
            getAccessToken={getAccessToken as unknown as () => Promise<string | undefined>} />
        </Section>

        <Section title="Pick your path">
          <Grid2>
            <Field label="Eyebrow"  value={c.path.eyebrow}  onChange={(v) => set('path', { ...c.path, eyebrow: v })} />
            <Field label="Title (line 1)" value={c.path.titleLine1} onChange={(v) => set('path', { ...c.path, titleLine1: v })} />
          </Grid2>
          <Field label="Title (line 2, italic gold)" value={c.path.titleLine2} onChange={(v) => set('path', { ...c.path, titleLine2: v })} />
          <Textarea label="Body" value={c.path.body} onChange={(v) => set('path', { ...c.path, body: v })} />
          <div className="border-t border-border pt-3 mt-3"><div className="eyebrow">Card 01 (Garments)</div></div>
          <Grid2>
            <Field label="Number" value={c.path.card01.num} onChange={(v) => set('path', { ...c.path, card01: { ...c.path.card01, num: v } })} />
            <Field label="Title"  value={c.path.card01.title} onChange={(v) => set('path', { ...c.path, card01: { ...c.path.card01, title: v } })} />
          </Grid2>
          <Textarea label="Body" value={c.path.card01.body} onChange={(v) => set('path', { ...c.path, card01: { ...c.path.card01, body: v } })} />
          <Field label="CTA label" value={c.path.card01.ctaLabel} onChange={(v) => set('path', { ...c.path, card01: { ...c.path.card01, ctaLabel: v } })} />
          <div className="border-t border-border pt-3 mt-3"><div className="eyebrow">Card 02 (DTF)</div></div>
          <Grid2>
            <Field label="Number" value={c.path.card02.num} onChange={(v) => set('path', { ...c.path, card02: { ...c.path.card02, num: v } })} />
            <Field label="Title"  value={c.path.card02.title} onChange={(v) => set('path', { ...c.path, card02: { ...c.path.card02, title: v } })} />
          </Grid2>
          <Textarea label="Body" value={c.path.card02.body} onChange={(v) => set('path', { ...c.path, card02: { ...c.path.card02, body: v } })} />
          <Field label="CTA label" value={c.path.card02.ctaLabel} onChange={(v) => set('path', { ...c.path, card02: { ...c.path.card02, ctaLabel: v } })} />
        </Section>

        <StepsSection title="How it works, Garments" block={c.garments} onChange={(b) => set('garments', b)} />

        <Section title="A look inside, Garment Designer">
          <Field label="Eyebrow" value={c.lookInsideGarments.eyebrow} onChange={(v) => set('lookInsideGarments', { ...c.lookInsideGarments, eyebrow: v })} />
          <Field label="Title"   value={c.lookInsideGarments.title}   onChange={(v) => set('lookInsideGarments', { ...c.lookInsideGarments, title: v })} />
          <Textarea label="Body" value={c.lookInsideGarments.body}    onChange={(v) => set('lookInsideGarments', { ...c.lookInsideGarments, body: v })} />
          <ImageField label="Screenshot" value={c.lookInsideGarments.imageUrl}
            onChange={(v) => set('lookInsideGarments', { ...c.lookInsideGarments, imageUrl: v })}
            getAccessToken={getAccessToken as unknown as () => Promise<string | undefined>} />
        </Section>

        <StepsSection title="How it works, DTF Transfers" block={c.dtf} onChange={(b) => set('dtf', b)} />

        <Section title="A look inside, DTF Builder">
          <Field label="Eyebrow" value={c.lookInsideDtf.eyebrow} onChange={(v) => set('lookInsideDtf', { ...c.lookInsideDtf, eyebrow: v })} />
          <Field label="Title"   value={c.lookInsideDtf.title}   onChange={(v) => set('lookInsideDtf', { ...c.lookInsideDtf, title: v })} />
          <Textarea label="Body" value={c.lookInsideDtf.body}    onChange={(v) => set('lookInsideDtf', { ...c.lookInsideDtf, body: v })} />
          <ImageField label="Screenshot" value={c.lookInsideDtf.imageUrl}
            onChange={(v) => set('lookInsideDtf', { ...c.lookInsideDtf, imageUrl: v })}
            getAccessToken={getAccessToken as unknown as () => Promise<string | undefined>} />
        </Section>

        <Section title="FAQ">
          <Grid2>
            <Field label="Eyebrow" value={c.faq.eyebrow} onChange={(v) => set('faq', { ...c.faq, eyebrow: v })} />
            <Field label="Title"   value={c.faq.title}   onChange={(v) => set('faq', { ...c.faq, title: v })} />
          </Grid2>
          <div className="space-y-2 mt-2">
            {c.faq.items.map((it, i) => (
              <div key={i} className="border border-border rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-ink-3">Item {i + 1}</div>
                  <button onClick={() => set('faq', { ...c.faq, items: c.faq.items.filter((_, x) => x !== i) })}
                    className="text-ink-3 hover:text-red-600 text-sm">Remove</button>
                </div>
                <Field label="Question" value={it.q} onChange={(v) => set('faq', { ...c.faq, items: c.faq.items.map((x, j) => j === i ? { ...x, q: v } : x) })} />
                <Textarea label="Answer" value={it.a} onChange={(v) => set('faq', { ...c.faq, items: c.faq.items.map((x, j) => j === i ? { ...x, a: v } : x) })} />
              </div>
            ))}
            <button onClick={() => set('faq', { ...c.faq, items: [...c.faq.items, { q: 'New question?', a: 'New answer.' }] })}
              className="text-xs text-gold-dark hover:underline">+ Add FAQ item</button>
          </div>
        </Section>

        <Section title="Final CTA">
          <Grid2>
            <Field label="Eyebrow"        value={c.finalCta.eyebrow}    onChange={(v) => set('finalCta', { ...c.finalCta, eyebrow: v })} />
            <Field label="Title (line 1)" value={c.finalCta.titleLine1} onChange={(v) => set('finalCta', { ...c.finalCta, titleLine1: v })} />
          </Grid2>
          <Field label="Title (line 2, italic gold)" value={c.finalCta.titleLine2} onChange={(v) => set('finalCta', { ...c.finalCta, titleLine2: v })} />
          <Textarea label="Body" value={c.finalCta.body} onChange={(v) => set('finalCta', { ...c.finalCta, body: v })} />
          <Grid2>
            <Field label="Primary CTA label"   value={c.finalCta.primaryCtaLabel}   onChange={(v) => set('finalCta', { ...c.finalCta, primaryCtaLabel: v })} />
            <Field label="Secondary CTA label" value={c.finalCta.secondaryCtaLabel} onChange={(v) => set('finalCta', { ...c.finalCta, secondaryCtaLabel: v })} />
          </Grid2>
        </Section>

        <Section title="Footer">
          <Grid2>
            <Field label="Parent site URL"   value={c.footer.parentUrl}        onChange={(v) => set('footer', { ...c.footer, parentUrl: v })} />
            <Field label="Contact email"     value={c.footer.contactEmail}     onChange={(v) => set('footer', { ...c.footer, contactEmail: v })} />
          </Grid2>
          <Field label="Copyright suffix" value={c.footer.copyrightSuffix} onChange={(v) => set('footer', { ...c.footer, copyrightSuffix: v })} />
        </Section>
      </div>

      <div className="sticky bottom-4 mt-6 flex justify-end max-w-4xl">
        <button onClick={save} disabled={busy} className="btn-gold shadow-lg">{busy ? 'Saving…' : 'Save changes'}</button>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true)
  return (
    <div className="card !p-0 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3 border-b border-border text-left hover:bg-surface">
        <div className="font-heading uppercase font-bold text-sm text-ink tracking-wider">{title}</div>
        <span className={'text-gold-dark text-xl leading-none transition-transform ' + (open ? '' : '-rotate-90')}>▾</span>
      </button>
      {open && <div className="p-5 space-y-3">{children}</div>}
    </div>
  )
}
function StepsSection({ title, block, onChange }: { title: string; block: PrintsPageContent['garments']; onChange: (b: PrintsPageContent['garments']) => void }) {
  return (
    <Section title={title}>
      <Grid2>
        <Field label="Eyebrow"        value={block.eyebrow}    onChange={(v) => onChange({ ...block, eyebrow: v })} />
        <Field label="Title (line 1)" value={block.titleLine1} onChange={(v) => onChange({ ...block, titleLine1: v })} />
      </Grid2>
      <Field label="Title (line 2, italic gold)" value={block.titleLine2} onChange={(v) => onChange({ ...block, titleLine2: v })} />
      <Textarea label="Intro / explainer copy" value={block.intro} onChange={(v) => onChange({ ...block, intro: v })} />
      <Field label="CTA label" value={block.ctaLabel} onChange={(v) => onChange({ ...block, ctaLabel: v })} />
      <div className="border-t border-border pt-3 mt-3"><div className="eyebrow">Steps</div></div>
      <div className="space-y-2">
        {block.steps.map((s, i) => (
          <div key={i} className="border border-border rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-ink-3">Step {i + 1}</div>
              {block.steps.length > 1 && (
                <button onClick={() => onChange({ ...block, steps: block.steps.filter((_, x) => x !== i) })}
                  className="text-ink-3 hover:text-red-600 text-sm">Remove</button>
              )}
            </div>
            <Grid2>
              <Field label="Number" value={s.num}   onChange={(v) => onChange({ ...block, steps: block.steps.map((x, j) => j === i ? { ...x, num: v } : x) })} />
              <Field label="Title"  value={s.title} onChange={(v) => onChange({ ...block, steps: block.steps.map((x, j) => j === i ? { ...x, title: v } : x) })} />
            </Grid2>
            <Textarea label="Body" value={s.body} onChange={(v) => onChange({ ...block, steps: block.steps.map((x, j) => j === i ? { ...x, body: v } : x) })} />
          </div>
        ))}
        <button onClick={() => onChange({ ...block, steps: [...block.steps, { num: `Step ${block.steps.length + 1}`, title: 'New step', body: '' }] })}
          className="text-xs text-gold-dark hover:underline">+ Add step</button>
      </div>
    </Section>
  )
}
function Grid2({ children }: { children: React.ReactNode }) { return <div className="grid md:grid-cols-2 gap-3">{children}</div> }
function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">{label}</div><input value={value} onChange={(e) => onChange(e.target.value)} className="input" /></div>
}
function Textarea({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">{label}</div><textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="input" /></div>
}

// Image field — shows the current image + lets the admin upload a replacement.
// Uses /api/files with kind='mockup' (same path mockups + studio media take).
function ImageField({ label, value, onChange, getAccessToken }: {
  label: string
  value: string
  onChange: (v: string) => void
  getAccessToken: () => Promise<string | undefined>
}) {
  const apiSrc = useApiSrc()
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)

  async function upload(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return
    setUploading(true)
    const reader = new FileReader()
    reader.onload = async () => {
      try {
        const up = await apiFetch<{ id: string; url: string }>(getAccessToken, '/api/files', {
          method: 'POST',
          body: JSON.stringify({ originalName: f.name, mimeType: f.type, base64: reader.result, kind: 'mockup' }),
        })
        onChange(up.url)
      } catch (err) { alert((err as Error).message) }
      finally { setUploading(false); e.target.value = '' }
    }
    reader.readAsDataURL(f)
  }

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1">{label}</div>
      <div className="flex items-start gap-3">
        <div className="w-40 h-24 bg-surface border border-border rounded overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img src={apiSrc(value)} alt={label} className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] uppercase text-ink-3">No image</span>}
        </div>
        <div className="flex-1 min-w-0">
          <input value={value} onChange={(e) => onChange(e.target.value)} className="input !text-xs font-mono" placeholder="/previews/...png or /api/files/.../download?sig=..." />
          <div className="flex items-center gap-2 mt-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline !text-xs px-3 py-1.5">{uploading ? 'Uploading…' : 'Upload new'}</button>
            {value && <button onClick={() => onChange('')} className="text-xs text-ink-3 hover:text-red-600">Clear</button>}
          </div>
        </div>
      </div>
    </div>
  )
}
